import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/Card';
import { TrendingUp, Users, AlertTriangle, DollarSign, Download, Trash2, Edit, Save } from 'lucide-react';
import { ApiTicketSales, api, DashboardData } from '../services/api';
import { formatCents } from '../utils/currency';
import styles from './EventDashboardScreen.module.css';

interface Forecast {
  event_id: number;
  event_title: string;
  ticket_price: number;
  sales: { valid_tickets: number; pending_tickets: number; cancelled_tickets: number };
  forecast: { current_revenue: number; potential_revenue: number; projected_revenue: number };
}

interface BudgetLine {
  id: number;
  description: string;
  amount: number;
  category: string;
  is_expense: boolean;
  is_approved: boolean;
  synced_from_tt: boolean;
}

interface Incident {
  id: number;
  incident_type: string;
  description: string;
  status: string;
  is_confidential: boolean;
  resolution_notes: string | null;
  created_at: string;
  reporter_name: string | null;
  assigned_to: number | null;
  assigned_to_name: string | null;
}

const BUDGET_CATEGORIES = ['venue', 'marketing', 'talent', 'equipment', 'other'] as const;

export function EventDashboardScreen() {
  const { eventId } = useParams<{ eventId: string }>();
  const [data, setData] = useState<DashboardData | null>(null);
  const [sales, setSales] = useState<ApiTicketSales | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [budgetLines, setBudgetLines] = useState<BudgetLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingLine, setEditingLine] = useState<BudgetLine | null>(null);
  const [newLine, setNewLine] = useState({
    description: '',
    amount: '',
    category: 'venue',
    isExpense: true
  });
  const [showIncidentEditForm, setShowIncidentEditForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
  const [incidentForm, setIncidentForm] = useState({
    status: 'new',
    resolutionNotes: '',
    staffId: ''
  });
  const [members, setMembers] = useState<{ id: number; display_name: string }[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [settlement, setSettlement] = useState<{
    id?: number;
    event_id: number;
    status: string;
    gross_cents: number;
    service_fee_cents: number;
    tax_cents: number;
    platform_fee_cents: number;
    host_payout_cents: number;
    expenses_cents: number;
    net_cents: number;
    preview?: boolean;
    paid_method?: string;
    paid_reference?: string;
  } | null>(null);
  // Settlement paid inputs
  const [paidMethod, setPaidMethod] = useState('');
  const [paidReference, setPaidReference] = useState('');

  useEffect(() => {
    fetchDashboard();
    fetchMembers();
  }, [eventId]);

  const fetchMembers = async () => {
    try {
      const membersRes = await api.members.list();
      setMembers(membersRes);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const fetchDashboard = async () => {
    setForbidden(false);
    try {
      const json = await api.events.dashboard(Number(eventId));
      setData(json);
      const salesRes = await api.analytics.event(Number(eventId));
      setSales(salesRes.sales);
      const incidentsRes = await api.incidents.list(Number(eventId));
      setIncidents(incidentsRes);
      const budgetRes = await api.budget.list(Number(eventId));
      setBudgetLines(budgetRes.lines);
      // Fetch forecast for admin users (gated by route, not here)
      try {
        const forecastRes = await api.budget.forecast(Number(eventId));
        setForecast(forecastRes);
      } catch (error) {
        // Non-admin users will get 403, ignore silently
        console.debug('Forecast not available (admin-only):', error);
      }
      // Fetch settlement for admin users
      try {
        const settlementRes = await api.settlements.get(Number(eventId));
        setSettlement(settlementRes);
      } catch (error) {
        console.debug('Settlement not available (admin-only):', error);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error instanceof Error && error.message === 'Forbidden') {
        setForbidden(true);
      }
      // Show error state so user knows something went wrong
      setData(null);
      setSales(null);
      setIncidents([]);
      setBudgetLines([]);
      setForecast(null);
      setSettlement(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportEvent = async () => {
    setActionError(null);
    try {
      const json = await api.reports.exportEvent(Number(eventId));
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-${eventId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export event:', error);
      setActionError('Failed to export event. Check console for details.');
    }
  };

  const handleApproveLine = async (lineId: number) => {
    setActionError(null);
    try {
      const updatedLine = await api.budget.approve(lineId);
      setBudgetLines(prev => prev.map(line => 
        line.id === lineId ? updatedLine : line
      ));
    } catch (error) {
      console.error('Failed to approve budget line:', error);
      setActionError('Failed to approve budget line. Check console for details.');
    }
  };

  const handleAddLine = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError(null);
    try {
      await api.budget.create({
        eventId: Number(eventId),
        description: newLine.description,
        amount: parseFloat(newLine.amount),
        category: newLine.category,
        isExpense: newLine.isExpense
      });
      setNewLine({ description: '', amount: '', category: 'venue', isExpense: true });
      setShowAddForm(false);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to add budget line:', error);
      setActionError('Failed to add budget line. Check console for details.');
    }
  };

  const handleEditLine = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLine) return;
    setActionError(null);
    try {
      await api.budget.update(editingLine.id, {
        description: newLine.description,
        amount: parseFloat(newLine.amount),
        category: newLine.category,
        isExpense: newLine.isExpense
      });
      setNewLine({ description: '', amount: '', category: 'venue', isExpense: true });
      setShowEditForm(false);
      setEditingLine(null);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to edit budget line:', error);
      setActionError('Failed to edit budget line. Check console for details.');
    }
  };

  const handleDeleteLine = async (lineId: number) => {
    setActionError(null);
    try {
      await api.budget.delete(lineId);
      setBudgetLines(prev => prev.filter(line => line.id !== lineId));
    } catch (error) {
      console.error('Failed to delete budget line:', error);
      setActionError('Failed to delete budget line. Check console for details.');
    }
  };

  const startEdit = (line: BudgetLine) => {
    setEditingLine(line);
    setNewLine({
      description: line.description,
      amount: line.amount.toString(),
      category: line.category,
      isExpense: line.is_expense
    });
    setShowEditForm(true);
  };

  const cancelEdit = () => {
    setShowEditForm(false);
    setEditingLine(null);
    setNewLine({ description: '', amount: '', category: 'venue', isExpense: true });
  };

  const startEditIncident = (incident: Incident) => {
    setEditingIncident(incident);
    setIncidentForm({
      status: incident.status,
      resolutionNotes: incident.resolution_notes || '',
      staffId: incident.assigned_to?.toString() || ''
    });
    setShowIncidentEditForm(true);
  };

  const cancelIncidentEdit = () => {
    setShowIncidentEditForm(false);
    setEditingIncident(null);
    setIncidentForm({ status: 'new', resolutionNotes: '', staffId: '' });
  };

  const handleUpdateStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingIncident) return;
    setActionError(null);
    try {
      await api.incidents.updateStatus(editingIncident.id, {
        status: incidentForm.status,
        resolutionNotes: incidentForm.resolutionNotes || undefined
      });
      if (incidentForm.staffId) {
        await api.incidents.assign(editingIncident.id, parseInt(incidentForm.staffId, 10));
      }
      cancelIncidentEdit();
      fetchDashboard();
    } catch (error) {
      console.error('Failed to update incident status:', error);
      setActionError('Failed to update incident status. Check console for details.');
    }
  };

  const handleSyncTickets = async () => {
    setActionError(null);
    try {
      await api.events.syncTickets(Number(eventId));
      fetchDashboard();
    } catch (error) {
      console.error('Failed to sync tickets:', error);
      setActionError('Failed to sync tickets. Check console for details.');
    }
  };

  const handleFinalizeSettlement = async () => {
    setActionError(null);
    try {
      await api.settlements.finalize(Number(eventId));
      fetchDashboard();
    } catch (error) {
      console.error('Failed to finalize settlement:', error);
      setActionError((error as Error).message || 'Failed to finalize settlement.');
    }
  };

  const handleApproveSettlement = async () => {
    setActionError(null);
    if (!settlement?.id) return;
    try {
      await api.settlements.approve(settlement.id);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to approve settlement:', error);
      setActionError((error as Error).message || 'Failed to approve settlement.');
    }
  };

  const handleMarkPaid = async () => {
    setActionError(null);
    if (!settlement?.id || !paidMethod.trim() || !paidReference.trim()) return;
    try {
      await api.settlements.paid(settlement.id, { method: paidMethod.trim(), reference: paidReference.trim() });
      setPaidMethod('');
      setPaidReference('');
      fetchDashboard();
    } catch (error) {
      console.error('Failed to mark paid:', error);
      setActionError((error as Error).message || 'Failed to mark settlement as paid.');
    }
  };

  if (loading) return <div className={styles.loading}>Loading dashboard...</div>;
  if (!data) return <div className={styles.error}>{forbidden ? "You don't have permission to view this dashboard" : "Event not found"}</div>;

  const { stats, budget, volunteers, approval } = data;

  return (
    <div className={styles.container}>
      {actionError && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={20} />
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className={styles.errorClose}>
            <span className={styles.errorCloseText}>×</span>
          </button>
        </div>
      )}
      <div className={styles.header}>
        <h1>Event Dashboard</h1>
        <button className={styles.exportButton} onClick={handleExportEvent}>
          <Download size={16} />
          <span>Export Event Data</span>
        </button>
      </div>
      
      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <TrendingUp size={24} />
          </div>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Tickets Sold</span>
            <span className={styles.statValue}>{stats.tickets_sold}</span>
          </div>
        </Card>
        
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <DollarSign size={24} />
          </div>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Revenue</span>
            <span className={styles.statValue}>£{Number(stats.revenue || 0).toFixed(2)}</span>
          </div>
        </Card>
        
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <Users size={24} />
          </div>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Volunteers</span>
            <span className={styles.statValue}>{stats.volunteers_count}</span>
          </div>
        </Card>
        
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <AlertTriangle size={24} />
          </div>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Incidents</span>
            <span className={styles.statValue}>{stats.incidents_count}</span>
          </div>
        </Card>
      </div>

      {/* Budget Breakdown */}
      <Card className={styles.section}>
        <h2>Budget Breakdown</h2>
        <div className={styles.budgetGrid}>
          {budget.map((item, i) => (
            <div key={i} className={styles.budgetItem}>
              <span className={styles.budgetLabel}>{item.category}</span>
              <div>
                <span className={`${styles.budgetAmount} ${item.is_expense ? styles.expense : styles.income}`}>
                  {item.is_expense ? '-' : '+'}£{Number(item.total).toFixed(2)}
                </span>
                <span className={styles.budgetCounts}>
                  {item.approved_count} approved, {item.pending_count} pending
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Forecast Stats (Admin Only) */}
      {forecast && (
        <Card className={styles.section}>
          <h2>Revenue Forecast</h2>
          <div className={styles.budgetGrid}>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Current Revenue</span>
              <span className={`${styles.budgetAmount} ${styles.currentRevenue}`}>£{forecast.forecast.current_revenue.toFixed(2)}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Potential Revenue</span>
              <span className={`${styles.budgetAmount} ${styles.potentialRevenue}`}>£{forecast.forecast.potential_revenue.toFixed(2)}</span>
            </div>

            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Projected Revenue</span>
              <span className={`${styles.budgetAmount} ${styles.projectedRevenue}`}>£{forecast.forecast.projected_revenue.toFixed(2)}</span>
            </div>
          </div>
          <div className={`${styles.budgetGrid} ${styles.budgetGridSection}`}>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Ticket Price</span>
              <span className={styles.budgetAmount}>£{forecast.ticket_price.toFixed(2)}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Valid Tickets</span>
              <span className={styles.budgetAmount}>{forecast.sales.valid_tickets}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Pending Tickets</span>
              <span className={styles.budgetAmount}>{forecast.sales.pending_tickets}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Cancelled Tickets</span>
              <span className={styles.budgetAmount}>{forecast.sales.cancelled_tickets}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Settlement (Admin Only) */}
      {settlement && (
        <Card className={styles.section}>
          <h2>Settlement {settlement.preview && <span className={styles.previewBadge}>Preview</span>}</h2>
          <div className={styles.budgetGrid}>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Status</span>
              <span className={styles.budgetAmount}>{settlement.status}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Gross</span>
              <span className={`${styles.budgetAmount} ${styles.income}`}>£{formatCents(settlement.gross_cents)}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Service Fee</span>
              <span className={`${styles.budgetAmount} ${styles.expense}`}>-£{formatCents(settlement.service_fee_cents)}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Platform Fee</span>
              <span className={`${styles.budgetAmount} ${styles.expense}`}>-£{formatCents(settlement.platform_fee_cents)}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Host Payout</span>
              <span className={`${styles.budgetAmount} ${styles.currentRevenue}`}>£{formatCents(settlement.host_payout_cents)}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Expenses</span>
              <span className={`${styles.budgetAmount} ${styles.expense}`}>-£{formatCents(settlement.expenses_cents)}</span>
            </div>
            <div className={styles.budgetItem}>
              <span className={styles.budgetLabel}>Net</span>
              <span className={`${styles.budgetAmount} ${styles.projectedRevenue}`}>£{formatCents(settlement.net_cents)}</span>
            </div>
          </div>

          {/* Settlement actions */}
          {settlement.preview && (
            <div style={{ marginTop: '12px' }}>
              <button className={styles.submitButton} onClick={handleFinalizeSettlement}>Finalize Settlement</button>
            </div>
          )}

          {settlement.status === 'pending' && !settlement.preview && (
            <div style={{ marginTop: '12px' }}>
              <button className={styles.submitButton} onClick={handleApproveSettlement}>Approve</button>
            </div>
          )}

          {settlement.status === 'approved' && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div>
                <label htmlFor="settlement-method" className={styles.formLabel}>Method</label>
                <input
                  id="settlement-method"
                  type="text"
                  className={styles.formInput}
                  placeholder="e.g. bank transfer"
                  value={paidMethod}
                  onChange={e => setPaidMethod(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="settlement-reference" className={styles.formLabel}>Reference</label>
                <input
                  id="settlement-reference"
                  type="text"
                  className={styles.formInput}
                  placeholder="e.g. TXN-12345"
                  value={paidReference}
                  onChange={e => setPaidReference(e.target.value)}
                />
              </div>
              <button className={styles.submitButton} onClick={handleMarkPaid} disabled={!paidMethod.trim() || !paidReference.trim()}>Mark Paid</button>
            </div>
          )}

          {settlement.status === 'paid' && settlement.paid_method && (
            <div style={{ marginTop: '12px' }}>
              <div className={styles.budgetItem}>
                <span className={styles.budgetLabel}>Paid via</span>
                <span className={styles.budgetAmount}>{settlement.paid_method} ({settlement.paid_reference})</span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Budget Lines */}
      <Card className={styles.section}>
        <h2>Budget Lines</h2>
        
        {/* Add Budget Line Form */}
        {showAddForm && (
          <div className={styles.addBudgetLineForm}>
            <form onSubmit={handleAddLine} className={styles.formColumn}>
              <div>
                <label htmlFor="budget-add-description" className={styles.formLabel}>Description</label>
                <input
                  id="budget-add-description"
                  type="text"
                  value={newLine.description}
                  onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                  placeholder="e.g., Venue rental, Marketing materials"
                  required
                  className={styles.formInput}
                />
              </div>
              <div>
                <label htmlFor="budget-add-amount" className={styles.formLabel}>Amount</label>
                <input
                  id="budget-add-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newLine.amount}
                  onChange={(e) => setNewLine({ ...newLine, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  className={styles.formInput}
                />
              </div>
              <div>
                <label htmlFor="budget-add-category" className={styles.formLabel}>Category</label>
                <select
                  id="budget-add-category"
                  value={newLine.category}
                  onChange={(e) => setNewLine({ ...newLine, category: e.target.value })}
                  className={styles.formInput}
                >
                  {BUDGET_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formCheckboxRow}>
                <input
                  type="checkbox"
                  id="isExpense"
                  checked={newLine.isExpense}
                  onChange={(e) => setNewLine({ ...newLine, isExpense: e.target.checked })}
                />
                <label htmlFor="isExpense" className={styles.formLabelBold}>Expense (uncheck for income)</label>
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.submitButton}>Add Line</button>
                <button type="button" onClick={() => setShowAddForm(false)} className={styles.cancelButton}>Cancel</button>
              </div>
            </form>
          </div>
        )}
        
        {/* Edit Budget Line Form */}
        {showEditForm && editingLine && (
          <div className={styles.addBudgetLineForm}>
            <form onSubmit={handleEditLine} className={styles.formColumn}>
              <div>
                <label htmlFor="budget-edit-description" className={styles.formLabel}>Description</label>
                <input
                  id="budget-edit-description"
                  type="text"
                  value={newLine.description}
                  onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                  placeholder="e.g., Venue rental, Marketing materials"
                  required
                  className={styles.formInput}
                />
              </div>
              <div>
                <label htmlFor="budget-edit-amount" className={styles.formLabel}>Amount</label>
                <input
                  id="budget-edit-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newLine.amount}
                  onChange={(e) => setNewLine({ ...newLine, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  className={styles.formInput}
                />
              </div>
              <div>
                <label htmlFor="budget-edit-category" className={styles.formLabel}>Category</label>
                <select
                  id="budget-edit-category"
                  value={newLine.category}
                  onChange={(e) => setNewLine({ ...newLine, category: e.target.value })}
                  className={styles.formInput}
                >
                  {BUDGET_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formCheckboxRow}>
                <input
                  type="checkbox"
                  id="isExpenseEdit"
                  checked={newLine.isExpense}
                  onChange={(e) => setNewLine({ ...newLine, isExpense: e.target.checked })}
                />
                <label htmlFor="isExpenseEdit" className={styles.formLabelBold}>Expense (uncheck for income)</label>
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.submitButton}>Save Changes</button>
                <button type="button" onClick={cancelEdit} className={styles.cancelButton}>Cancel</button>
              </div>
            </form>
          </div>
        )}
        
        {!showAddForm && !showEditForm && (
          <button 
            onClick={() => setShowAddForm(true)}
            className={styles.addButton}
          >
            + Add Budget Line
          </button>
        )}

        <div className={styles.budgetLinesList}>
          {budgetLines.map((line) => (
            <div key={line.id} className={styles.budgetLineItem}>
              <div className={styles.budgetLineInfo}>
                <span className={styles.budgetLineDescription}>{line.description}</span>
                <span className={styles.budgetLineCategory}>{line.category}</span>
              </div>
              <div className={styles.budgetLineAmount}>
                <span className={`${styles.budgetAmount} ${line.is_expense ? styles.expense : styles.income}`}>
                  {line.is_expense ? '-' : '+'}£{Number(line.amount).toFixed(2)}
                </span>
                {line.is_approved ? (
                  <span className={styles.approvedBadge}>Approved</span>
                ) : (
                  <button 
                    className={styles.approveButton}
                    onClick={() => handleApproveLine(line.id)}
                  >
                    Approve
                  </button>
                )}
                <button 
                  onClick={() => startEdit(line)}
                  className={styles.editButton}
                  title="Edit budget line"
                >
                  <Edit size={14} />
                </button>
                <button 
                  onClick={() => handleDeleteLine(line.id)}
                  className={styles.deleteButton}
                  title="Delete budget line"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Ticket Sales */}
      {sales && (
        <Card className={styles.section}>
          <div className={styles.ticketSalesHeader}>
            <h2 className={styles.ticketSalesTitle}>Ticket Sales</h2>
            <button
              onClick={handleSyncTickets}
              className={styles.syncButton}
            >
              Sync Tickets
            </button>
          </div>
          <div className={styles.approvalGrid}>
            <div className={styles.approvalItem}>
              <span className={styles.approvalLabel}>Total Tickets</span>
              <span className={styles.approvalValue}>{sales.total_tickets}</span>
            </div>
            <div className={styles.approvalItem}>
              <span className={styles.approvalLabel}>Valid</span>
              <span className={`${styles.approvalValue} ${styles.approved}`}>{sales.valid_tickets}</span>
            </div>
            <div className={styles.approvalItem}>
              <span className={styles.approvalLabel}>Checked In</span>
              <span className={styles.approvalValue}>{sales.checked_in}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Volunteer Breakdown */}
      <Card className={styles.section}>
        <h2>Volunteer Coverage</h2>
        <div className={styles.volunteerGrid}>
          {volunteers.map((v, i) => (
            <div key={i} className={styles.volunteerItem}>
              <span className={styles.volunteerRole}>{v.role}</span>
              <span className={styles.volunteerCount}>{v.count} staff</span>
              <span className={styles.volunteerHours}>{v.planned_hours} hours planned</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Approval Status */}
      {approval && (
        <Card className={styles.section}>
          <h2>Approval Status</h2>
          <div className={styles.approvalGrid}>
            <div className={styles.approvalItem}>
              <span className={styles.approvalLabel}>Approved</span>
              <span className={`${styles.approvalValue} ${styles.approved}`}>{approval.approved}</span>
            </div>
            <div className={styles.approvalItem}>
              <span className={styles.approvalLabel}>Pending</span>
              <span className={`${styles.approvalValue} ${styles.pending}`}>{approval.pending}</span>
            </div>
            <div className={styles.approvalItem}>
              <span className={styles.approvalLabel}>Total</span>
              <span className={styles.approvalValue}>{approval.total}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Incidents */}
      {incidents.length > 0 && (
        <Card className={styles.section}>
          <h2>Incidents</h2>
          <div className={styles.incidentList}>
            {incidents.map((incident) => (
              <div key={incident.id} className={styles.incidentItem}>
                <div className={styles.incidentHeader}>
                  <span className={styles.incidentType}>{incident.incident_type}</span>
                  <span className={`${styles.incidentStatus} ${incident.status === 'resolved' ? styles.resolved : styles.open}`}>
                    {incident.status}
                  </span>
                </div>
                <p className={styles.incidentDescription}>{incident.description}</p>
                {incident.reporter_name && (
                  <span className={styles.incidentReporter}>Reported by: {incident.reporter_name}</span>
                )}
                {incident.assigned_to_name && (
                  <span className={styles.incidentReporter}>Assigned to: {incident.assigned_to_name}</span>
                )}
                <div className={styles.incidentActions}>
                  <button
                    onClick={() => startEditIncident(incident)}
                    className={styles.editButton}
                    title="Edit incident status"
                  >
                    <Edit size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Edit Incident Form */}
      {showIncidentEditForm && editingIncident && (
        <Card className={styles.section}>
          <h2>Edit Incident</h2>
          <form onSubmit={handleUpdateStatus} className={styles.formColumn}>
            <div>
              <label htmlFor="incident-status" className={styles.formLabel}>Status</label>
              <select
                id="incident-status"
                value={incidentForm.status}
                onChange={(e) => setIncidentForm({ ...incidentForm, status: e.target.value })}
                className={styles.formInput}
              >
                <option value="new">New</option>
                <option value="reviewing">Reviewing</option>
                <option value="resolved">Resolved</option>
                <option value="reopen">Reopen</option>
              </select>
            </div>
            <div>
              <label htmlFor="incident-notes" className={styles.formLabel}>Resolution Notes</label>
              <textarea
                id="incident-notes"
                value={incidentForm.resolutionNotes}
                onChange={(e) => setIncidentForm({ ...incidentForm, resolutionNotes: e.target.value })}
                placeholder="Enter resolution notes..."
                rows={3}
                className={styles.formTextarea}
              />
            </div>
            <div>
              <label htmlFor="incident-assign" className={styles.formLabel}>Assign to Staff</label>
              <select
                id="incident-assign"
                value={incidentForm.staffId}
                onChange={(e) => setIncidentForm({ ...incidentForm, staffId: e.target.value })}
                className={styles.formInput}
              >
                <option value="">Unassigned</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>{member.display_name}</option>
                ))}
              </select>
            </div>
            <div className={styles.formActions}>
              <button type="submit" className={styles.submitButton}>
                <Save size={16} />
                <span>Update Status</span>
              </button>
              <button type="button" onClick={cancelIncidentEdit} className={styles.cancelButton}>Cancel</button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
